/**
 * Backward-compatible multi-step form helpers.
 *
 * Production rule: forms WITHOUT stepMode/steps must behave as single-page.
 * Do NOT migrate existing Firebase records — use runtime defaults.
 *
 * Schema (optional on form document):
 *   stepMode: boolean   // true only when multi-step is active
 *   steps: Array<{
 *     id: string,
 *     title: string,
 *     description: string,
 *     order: number,
 *     fieldIds: string[]  // references content.fields[].id
 *   }>
 */

/** @typedef {{ id: string, title?: string, description?: string, order?: number, fieldIds?: string[] }} FormStep */

export const DEFAULT_STEP_TITLE = "Untitled Step";

/** Unique step id (avoids Date.now collisions when creating steps back-to-back). */
export const createStepId = () =>
  `step_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;

/**
 * Strict check — only explicit true enables multi-step.
 * undefined / false / missing ⇒ single-page (existing production behavior).
 */
export const isMultiStepForm = (form) => form?.stepMode === true;

/** All field definitions (source of truth). */
export const getFormFields = (form) =>
  Array.isArray(form?.content?.fields) ? form.content.fields : [];

/** Map of fieldId → field object. */
export const getFieldMap = (form) => {
  const map = Object.create(null);
  getFormFields(form).forEach((f) => {
    if (f?.id) map[f.id] = f;
  });
  return map;
};

/** Steps sorted by order (stable). Empty if none. */
export const getOrderedSteps = (form) => {
  const steps = Array.isArray(form?.steps) ? [...form.steps] : [];
  return steps.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
};

/**
 * Resolve fields for a step via fieldIds (no duplicated field objects).
 * Skips missing ids (orphan references).
 */
export const getFieldsForStep = (form, step) => {
  if (!step) return [];
  const byId = getFieldMap(form);
  const ids = Array.isArray(step.fieldIds) ? step.fieldIds : [];
  return ids.map((id) => byId[id]).filter(Boolean);
};

/** Fields that appear in content.fields but in no step.fieldIds. */
export const getOrphanFieldIds = (form) => {
  const assigned = new Set();
  getOrderedSteps(form).forEach((step) => {
    (step.fieldIds || []).forEach((id) => assigned.add(id));
  });
  return getFormFields(form)
    .map((f) => f.id)
    .filter((id) => id && !assigned.has(id));
};

/** Field ids listed in more than one step. */
export const getDuplicateStepFieldIds = (form) => {
  const seen = new Set();
  const dupes = new Set();
  getOrderedSteps(form).forEach((step) => {
    (step.fieldIds || []).forEach((id) => {
      if (!id) return;
      if (seen.has(id)) dupes.add(id);
      else seen.add(id);
    });
  });
  return [...dupes];
};

/** Create a new step object (does not mutate form). */
export const createStep = ({
  id,
  title = DEFAULT_STEP_TITLE,
  description = "",
  order = 1,
  fieldIds = [],
} = {}) => ({
  id: id || createStepId(),
  title: title || DEFAULT_STEP_TITLE,
  description: description || "",
  order: Number.isFinite(order) ? order : 1,
  fieldIds: Array.isArray(fieldIds) ? [...fieldIds] : [],
});

/**
 * Enable multi-step: Step 1 gets all current field ids in existing order.
 * Idempotent if already enabled with steps — returns form unchanged.
 * Pure: returns a shallow-cloned form object.
 */
export const enableMultiStep = (form) => {
  if (!form) return form;
  if (isMultiStepForm(form) && getOrderedSteps(form).length > 0) {
    return { ...form, stepMode: true };
  }

  const fieldIds = getFormFields(form).map((f) => f.id).filter(Boolean);
  const existingSteps = getOrderedSteps(form);

  // Re-enable: restore previous steps if present
  if (existingSteps.length > 0) {
    return {
      ...form,
      stepMode: true,
      steps: existingSteps,
    };
  }

  const step1 = createStep({
    title: "Step 1",
    order: 1,
    fieldIds,
  });

  return {
    ...form,
    stepMode: true,
    steps: [step1],
  };
};

/**
 * Disable multi-step without deleting step config (allows restore).
 */
export const disableMultiStep = (form) => {
  if (!form) return form;
  return {
    ...form,
    stepMode: false,
  };
};

/** Renormalize step.order to 1..n based on current array order. */
export const normalizeStepOrders = (steps = []) =>
  steps.map((step, index) => ({
    ...step,
    order: index + 1,
    fieldIds: Array.isArray(step.fieldIds) ? [...step.fieldIds] : [],
  }));

/** Append an empty step at the end. */
export const addStepToForm = (form, title = DEFAULT_STEP_TITLE) => {
  const steps = getOrderedSteps(form);
  const next = createStep({
    title,
    order: steps.length + 1,
    fieldIds: [],
  });
  return {
    ...form,
    stepMode: true,
    steps: normalizeStepOrders([...steps, next]),
  };
};

/** Rename a step by id. */
export const renameStep = (form, stepId, title) => {
  const steps = getOrderedSteps(form).map((s) =>
    s.id === stepId ? { ...s, title: title || DEFAULT_STEP_TITLE } : s
  );
  return { ...form, steps };
};

/**
 * Delete a step. Orphaned fields are moved to the first remaining step
 * (or left unassigned if no steps remain — caller should handle).
 */
export const deleteStep = (form, stepId) => {
  const steps = getOrderedSteps(form);
  const target = steps.find((s) => s.id === stepId);
  if (!target) return form;

  const remaining = steps.filter((s) => s.id !== stepId);
  const orphanIds = target.fieldIds || [];

  if (remaining.length === 0) {
    return {
      ...form,
      stepMode: false,
      steps: [],
    };
  }

  const [first, ...rest] = remaining;
  const mergedFirst = {
    ...first,
    fieldIds: [...(first.fieldIds || []), ...orphanIds],
  };

  return {
    ...form,
    stepMode: true,
    steps: normalizeStepOrders([mergedFirst, ...rest]),
  };
};

/** Reorder steps by array of step ids. */
export const reorderSteps = (form, orderedStepIds) => {
  const byId = Object.fromEntries(
    getOrderedSteps(form).map((s) => [s.id, s])
  );
  const next = orderedStepIds.map((id) => byId[id]).filter(Boolean);
  // Append any steps missing from orderedStepIds
  getOrderedSteps(form).forEach((s) => {
    if (!orderedStepIds.includes(s.id)) next.push(s);
  });
  return {
    ...form,
    steps: normalizeStepOrders(next),
  };
};

/** Move a field id into a target step (removes from other steps). */
export const moveFieldToStep = (form, fieldId, targetStepId) => {
  if (!fieldId || !targetStepId) return form;
  const steps = getOrderedSteps(form).map((s) => {
    const without = (s.fieldIds || []).filter((id) => id !== fieldId);
    if (s.id === targetStepId) {
      return { ...s, fieldIds: [...without, fieldId] };
    }
    return { ...s, fieldIds: without };
  });
  return { ...form, steps };
};

/** Append field to a step if not already present anywhere. */
export const appendFieldToStep = (form, fieldId, stepId) => {
  if (!fieldId) return form;
  const steps = getOrderedSteps(form);
  if (steps.length === 0) {
    return enableMultiStep({
      ...form,
      content: {
        ...form.content,
        fields: getFormFields(form),
      },
    });
  }

  const targetId = stepId || steps[0].id;
  // Remove from all, then add to target
  return moveFieldToStep(form, fieldId, targetId);
};

/** Remove a field id from all steps (e.g. when field deleted). */
export const removeFieldFromAllSteps = (form, fieldId) => {
  if (!fieldId || !Array.isArray(form?.steps)) return form;
  const steps = getOrderedSteps(form).map((s) => ({
    ...s,
    fieldIds: (s.fieldIds || []).filter((id) => id !== fieldId),
  }));
  return { ...form, steps };
};

/**
 * Reorder a field within a step's fieldIds (builder canvas up/down).
 * direction: "up" | "down"
 */
export const reorderFieldInStep = (form, stepId, fieldId, direction) => {
  if (!stepId || !fieldId) return form;
  const steps = getOrderedSteps(form).map((s) => {
    if (s.id !== stepId) return s;
    const ids = [...(s.fieldIds || [])];
    const index = ids.indexOf(fieldId);
    if (index === -1) return s;
    if (direction === "up" && index > 0) {
      [ids[index - 1], ids[index]] = [ids[index], ids[index - 1]];
    } else if (direction === "down" && index < ids.length - 1) {
      [ids[index], ids[index + 1]] = [ids[index + 1], ids[index]];
    }
    return { ...s, fieldIds: ids };
  });
  return { ...form, steps };
};

/**
 * Publish-time validation when stepMode is true.
 * Returns { ok: true } or { ok: false, errors: string[] }.
 */
export const validateMultiStepConfig = (form) => {
  if (!isMultiStepForm(form)) {
    return { ok: true, errors: [] };
  }

  const errors = [];
  const steps = getOrderedSteps(form);

  if (steps.length === 0) {
    errors.push("Multi-step forms need at least one step.");
  }

  steps.forEach((step, i) => {
    if (!step.id) errors.push(`Step ${i + 1} is missing an id.`);
    if (!step.title || !String(step.title).trim()) {
      errors.push(`Step ${i + 1} needs a title.`);
    }
  });

  const stepIds = steps.map((s) => s.id).filter(Boolean);
  if (new Set(stepIds).size !== stepIds.length) {
    errors.push("Step ids must be unique.");
  }

  const orphans = getOrphanFieldIds(form);
  if (orphans.length > 0) {
    errors.push(
      `${orphans.length} field(s) are not assigned to any step.`
    );
  }

  const dupes = getDuplicateStepFieldIds(form);
  if (dupes.length > 0) {
    errors.push("Some fields are assigned to more than one step.");
  }

  // Dangling fieldIds that don't exist in content.fields
  const fieldMap = getFieldMap(form);
  steps.forEach((step, i) => {
    const missing = (step.fieldIds || []).filter((id) => !fieldMap[id]);
    if (missing.length > 0) {
      errors.push(
        `Step ${i + 1} references ${missing.length} missing field(s).`
      );
    }
  });

  return { ok: errors.length === 0, errors };
};

/**
 * Fields to render for public/preview given stepMode + index.
 * Single-page ⇒ all fields. Multi-step ⇒ current step fields only.
 */
export const getRenderableFields = (form, currentStepIndex = 0) => {
  if (!isMultiStepForm(form)) {
    return getFormFields(form);
  }
  const steps = getOrderedSteps(form);
  if (steps.length === 0) {
    // Corrupt config — safe fallback to single-page field list
    return getFormFields(form);
  }
  const idx = Math.min(
    Math.max(0, currentStepIndex),
    steps.length - 1
  );
  return getFieldsForStep(form, steps[idx]);
};
